import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { MdDelete } from "react-icons/md";
import { useProject } from "../api/getProjectsList";
import moment from "moment";
import { Menu, MenuItem } from '@szhsin/react-menu';
import { useMemo, useState } from "react";
import { TiEye } from "react-icons/ti";
import '@szhsin/react-menu/dist/index.css';
import '@szhsin/react-menu/dist/transitions/slide.css';
import { HiDotsVertical } from "react-icons/hi"
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { selectPageSize } from "../../../app/PageSlice";
import { ProjectDto } from "../types";
import PaginationControls from "../../../components/Table/PaginationControls";
import { BiEdit } from "react-icons/bi";
import { useDisclosure } from '@mantine/hooks';
import { Modal } from '@mantine/core';
import { useTranslation } from "react-i18next";
import { Skeleton } from 'primereact/skeleton';
import { Button } from "@mui/material";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useDeleteProject } from "../api/deleteProjects";
import TableLoader from "../../../components/Loader/TableLoader";
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import type { MRT_ColumnDef } from 'mantine-react-table'; // If using TypeScript (optional, but recommended)
import { getStatusByLang, getStatusColor, getStatusLibelle, truncateText } from "../../../utils/monthDiff";
import { Notifications } from "../../../components/notifications";
import { selectCurrentUser } from "../../../auth/AuthUserSlice";


interface Props {
  queryFilter?: string;
  onlyCurrentValidator?: string;
  defaultPageSize?: number;
  hiddenPagination?: Boolean;
}

interface RowProps {
  demande: ProjectDto;
}

  //If using TypeScript, define the shape of your data (optional, but recommended)
  interface Person {
    name: string;
    age: number;
  }
export function ProjectsListTable({ queryFilter, defaultPageSize } : Props) {
  
  const [opened, { open, close }] = useDisclosure(false);

  const pageSize = useSelector(selectPageSize);
  const [projectId, setProjectId] = useState("");
  const { t, i18n } = useTranslation("common");

  // Get current language
  let lang = i18n.language;
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);

  const deleteProjectsApi = useDeleteProject();
  function deleteProjects(id:string) {
    deleteProjectsApi
      .mutateAsync(id)
      .then(() => {
        Notifications.success("Projet supprimé avec succès.");
        close();
      })
      .catch((e) => {
        Notifications.error("Erreur lors de la suppression du projet.");
        console.error(e);
      });
  }

  const [pageNumber, setPageNumber] = useState(1);
 
  const { data: projectsResponse, isLoading, isError,isSuccess } = useProject({
    sortOrder: [{desc: true, id: "createdOn" }],
    pageSize: defaultPageSize?? pageSize,
    pageNumber: pageNumber,
    filters: `createdBy==${currentUser.preferred_username}||${queryFilter}`,
    // hasArtificialDelay: true,
  });

  const projectsData = projectsResponse?.data;
  const pagination = projectsResponse?.pagination;

  //column definitions - strongly typed if you are using TypeScript (optional, but recommended)
  const columns: MRT_ColumnDef<ProjectDto>[] = [
      {
        accessorKey: 'name', //simple recommended way to define a column
        header: t('projet.generalInfos.name')
      },
      {
        header: t('projet.generalInfos.periode'),
        accessorFn: (originalRow) => {
          return moment(originalRow.startDate).format("DD/MM/YYYY") + " - " + moment(originalRow.endDate).format("DD/MM/YYYY");
        }
      },
      {
        accessorKey: 'localisationCountry',
        header: t('projet.generalInfos.locationCountry')
      },
      {
        accessorKey: 'localisationCity',
        header: t('projet.generalInfos.locationCity')
      },
      {
        accessorFn: (originalRow) => {
          return <Menu 
          className={""}
          // style={{padding:"0px !important"}}  
          menuButton={
            <Button 
              style={{color:"#ccc", borderRadius:"35px",minWidth:"35px",minHeight:"35px"}} 
              size="small"  
              variant="text"
            >
            <HiDotsVertical style={{color:"#333"}} />
          </Button>} 
          transition
        >
          <MenuItem onClick={() => navigate("/detail/projects/" + originalRow.id )}><TiEye className="mr-2" /><span>{ t('app.consulter') }</span></MenuItem>
          <MenuItem onClick={() => navigate("/edit/projects/" + originalRow.id )} /*label="Confirm"*/ ><BiEdit />{ t('app.update') }</MenuItem>
          <MenuItem onClick={()=>{
              setProjectId(originalRow?.id);
              open();
            }}
            // label="Confirm" 
          ><MdDelete />{ t('app.delete') }</MenuItem>
        </Menu>
        }, //alternate way
        id: 'action', //id required if you use accessorFn instead of accessorKey
        header: 'Action',
        Header: "Action", //optional custom markup
      },
  ];

  const table = useMantineReactTable({
    columns,
    data: projectsData??[],
    enablePagination: false,
    enableBottomToolbar: false,
    enableGlobalFilter: false,
    enableSorting: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnDragging: false,
    enableHiding: false,
    enableRowSelection: false,
    /* columnFilterDisplayMode: 'popover',
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom', */
  });


  return (
    <>
    {/* <MantineReactTable 
      table={table}
    />   */}
    <div className="row">
        {
           (projectsData && projectsData.length > 0)
           ?
           projectsData.map((projet, index) => (
              <div key={index} className="col-md-4">
                <div className="card mb-3 card-style" >
                      <div className="card-body">
                        <h5 className="card-title uppercase h-49" style={{ fontWeight: "bold" }} >{ projet.name }</h5>
                        <p className="card-text text-justify h-40">{ truncateText(projet.summary, 40) }</p>
                        <p className="card-text">{ t('projet.generalInfos.periode') }: <small className="text-muted "> { moment(projet.startDate).format("DD/MM/YYYY") } - { moment(projet.endDate).format("DD/MM/YYYY") } </small></p>
                        <p className="card-text">{ t('projet.generalInfos.localisation') }: <small className="text-muted">{projet.locationCity}-{projet.locationCountry}</small></p>
                        <p className="card-text">{/*{ t('projet.generalInfos.statut') }:&nbsp; */}
                          <span className={ `badge ${getStatusColor(projet.status!)} uppercase ml-auto` } >{ getStatusByLang(projet.status, lang) }</span>
                        </p>
                        <br/>
                        <div className="h-80">
                            <div className="d-flex">
                                <NavLink 
                                    className={ "btn-more w-100" }
                                    to={`/detail/projects/${projet.id}`}
                                  >
                                  { t('app.moreInfos') } 
                                </NavLink>
                            </div>
                            <div className="d-flex mt-2">
                                { projet.status === "Draft" &&
                                  <NavLink 
                                      className={"btn-update w-100"}
                                      to={`/update/projects/${projet.id}`}
                                    >
                                    { t('app.update') } 
                                  </NavLink>
                                }
                            </div>
                        </div>
                      </div>
                </div>
              </div>
            ))
            :
            <>
                <span className="text-center" style={{ marginTop: "100px", marginBottom: "100px" }}>{ t('app.noneResult') }.</span>
            </> 
        }
        
    </div>
    <PaginationControls
      pageNumber={pageNumber}
      totalPages={pagination?.totalPages}
      onPageNumberChange={setPageNumber}
    />
    
    <Modal opened={opened} onClose={close} title={ t('app.confirmAction') } centered withCloseButton>
      <span>{ t('app.confirmDelete') } projet ?</span>
      <div className="d-flex mt-3">
        <Button 
          className="ml-auto"
          style={{color:"white",background:"green",border:"1px solid green"}}
          variant="contained"
          onClick={() => deleteProjects(projectId)}>{ t('app.confirm') }</Button>
        <div className="col-1"></div>
        <Button 
          variant="outlined"
          style={{color:"gray",border:"1px solid gray"}}
          onClick={close}>{ t('app.cancel') }</Button> 
      </div>
    </Modal>  
  </>
  );
}
